import { useCallback, useEffect, useState } from "react";
import { PauseCircle, Play, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { getPendingInstalls, discardPendingInstall, installAgentModel, getHfRepoSize } from "@/lib/agent";

const gb = (b) => (b / 1e9).toFixed(2);

function PendingRow({ item, onResume, onDiscard }) {
  const [total, setTotal] = useState(null);
  useEffect(() => { getHfRepoSize(item.repo_id).then(setTotal).catch(() => {}); }, [item.repo_id]);
  const pct = total ? Math.min(100, (item.bytes / total) * 100).toFixed(1) : null;
  const slug = item.repo_id.replace(/[^a-z0-9]/gi, "-");
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#262626] py-2 first:border-t-0" data-testid={`pending-${slug}`}>
      <div className="min-w-0">
        <div className="text-sm text-white">{item.name || item.repo_id}</div>
        <div className="font-mono text-[11px] text-[#a3a3a3]">
          {item.repo_id} · {gb(item.bytes)} GB{total ? ` de ${gb(total)} GB (${pct}%)` : ""} ·{" "}
          {item.status === "error" ? <span className="text-red-300">interrumpida</span> : "en pausa"}
        </div>
        {item.error && <div className="mt-0.5 max-w-3xl truncate font-mono text-[10px] text-[#737373]" title={item.error}>{item.error}</div>}
      </div>
      <div className="flex gap-2">
        <button onClick={() => onResume(item)} data-testid={`pending-resume-${slug}`} className="inline-flex items-center gap-1.5 rounded-sm bg-emerald-500 px-3 py-1 text-xs font-medium text-black hover:bg-emerald-400">
          <Play className="h-3 w-3" />Reanudar
        </button>
        <button onClick={() => onDiscard(item)} data-testid={`pending-discard-${slug}`} className="inline-flex items-center gap-1.5 rounded-sm border border-[#404040] px-3 py-1 text-xs text-[#a3a3a3] hover:text-white">
          <Trash2 className="h-3 w-3" />Descartar
        </button>
      </div>
    </div>
  );
}

export function PendingDownloads() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => getPendingInstalls().then((d) => setItems(d?.items || [])).catch(() => setItems([])), []);

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [load]);

  const resume = (item) => {
    toast.success("Reanudando descarga donde se quedó…");
    setItems((xs) => xs.filter((x) => x.repo_id !== item.repo_id));
    installAgentModel(item.payload)
      .then((d) => (d?.ok ? toast.success(`Modelo instalado: ${item.name}`) : d?.status !== "CANCELLED" && toast.error(d?.error || "La descarga se interrumpió")))
      .catch(() => {})
      .finally(load);
  };

  const discard = async (item) => {
    if (!window.confirm(`¿Descartar ${item.name}? Se borrarán ${gb(item.bytes)} GB descargados.`)) return;
    const d = await discardPendingInstall(item.repo_id).catch((e) => ({ ok: false, error: e?.message }));
    d?.ok ? toast.success(d.message) : toast.error(d?.error || "No se pudo descartar");
    load();
  };

  if (!items.length) return null;
  return (
    <div className="mb-6 rounded-sm border border-amber-500/30 bg-amber-500/5 p-4" data-testid="pending-downloads">
      <div className="mb-2 flex items-center gap-2 text-sm text-amber-300">
        <PauseCircle className="h-4 w-4" />
        <span className="font-heading font-semibold">Descargas pendientes</span>
      </div>
      {items.map((it) => <PendingRow key={it.repo_id} item={it} onResume={resume} onDiscard={discard} />)}
    </div>
  );
}
